// Core vocabulary of VERA Studio: areas, project scopes, roles and permissions.

import { CLIENT_PACKS, packOfRole } from "./clientPacks";

export type AreaId =
  | "home" | "workspace" | "proposals" | "workflows" | "planner" | "files" | "pages" | "connections"
  | "projects" | "setup" | "training" | "models" | "engineering" | "security" | "operations"
  | "records" | "field" | "activity" | "settings";

export type ScopeId = "all" | "investor" | "consumer" | "vds" | "marine" | "dama" | "aecch" | "aecus" | "veraai" | "edutask";

export type Access = "full" | "view" | "none";

export type Profile = {
  key: string;
  label: string;
  areas: AreaId[];
  scopes: ScopeId[];
  external: boolean;
  projects: Access;
  models: Access;
  engineering: Access;
  publishPages: boolean;
  approveConnections: boolean;
  reviewTraining: boolean;
  /** A customer company with its own CRM: the calm look, Home with its records, Records, Activity, Build mode. */
  crm?: boolean;
  /** The client pack it comes from (see clientPacks.ts). */
  pack?: string;
};

export const AREAS: { id: AreaId; label: string; icon: string }[] = [
  { id: "home", label: "Home", icon: "home" },
  { id: "workspace", label: "Workspace", icon: "vera" },
  { id: "records", label: "Records", icon: "grid" },
  { id: "field", label: "From the field", icon: "img" },
  { id: "activity", label: "Activity", icon: "history" },
  { id: "proposals", label: "Proposals", icon: "doc" },
  { id: "workflows", label: "Workflows", icon: "sparkle" },
  { id: "planner", label: "Planner", icon: "board" },
  { id: "files", label: "Files", icon: "files" },
  { id: "pages", label: "Pages", icon: "page" },
  { id: "connections", label: "Connections", icon: "plug" },
  { id: "projects", label: "Projects", icon: "projects" },
  { id: "setup", label: "Client Setup", icon: "setup" },
  { id: "training", label: "Training Lab", icon: "training" },
  { id: "models", label: "Models", icon: "models" },
  { id: "engineering", label: "Engineering", icon: "engineering" },
  { id: "security", label: "Security", icon: "security" },
  { id: "operations", label: "Operations", icon: "operations" },
  { id: "settings", label: "Settings", icon: "setup" },
];

export const INTERNAL_SCOPES: ScopeId[] = ["all", "consumer", "vds", "marine", "dama", "veraai"];
/** "All of VERANeural" is the founders' workspace: only Founder and Executive can open it. Everyone else starts on their first project. */
const TEAM_SCOPES: ScopeId[] = INTERNAL_SCOPES.filter((s) => s !== "all");
const BASE: AreaId[] = ["home", "workspace", "planner", "files", "connections"];
/** VERANeural's own team: VERANeural HQ's records (platforms, tasks, notes, pipeline…), its Brain and Activity, on top of the base. */
const TEAM: AreaId[] = [...BASE, "records", "activity", "settings"];

const p = (o: Partial<Profile> & Pick<Profile, "key" | "label">): Profile => ({
  areas: BASE, scopes: INTERNAL_SCOPES, external: false, projects: "none", models: "none", engineering: "none",
  publishPages: false, approveConnections: false, reviewTraining: false, ...o,
});

/**
 * Studio profiles, keyed by the Microsoft Entra ID app role that grants them.
 * Create these app roles on the "VERA Studio" app registration and assign people or groups to them.
 */
export const PROFILES: Record<string, Profile> = {
  "Studio.Founder": p({ key: "founder", label: "Founder", areas: [...TEAM, "proposals", "workflows", "pages", "projects", "setup", "training", "models", "engineering", "security", "operations"], projects: "full", models: "full", engineering: "full", publishPages: true, approveConnections: true, reviewTraining: true , crm: true}),
  "Studio.Executive": p({ key: "executive", label: "Executive", areas: [...TEAM, "pages", "projects", "setup", "operations"], projects: "view", publishPages: true , crm: true}),
  "Studio.Operations": p({ key: "operations", label: "Operations", scopes: TEAM_SCOPES, areas: [...TEAM, "pages", "projects", "setup", "operations"], projects: "view" , crm: true}),
  "Studio.Psychologist": p({ key: "psychologist", label: "Psychologist", scopes: ["consumer", "veraai"], areas: [...TEAM, "training"], reviewTraining: true , crm: true}),
  "Studio.Engineering": p({ key: "engineering", label: "Engineering", scopes: TEAM_SCOPES, areas: [...TEAM, "projects", "setup", "models", "engineering"], projects: "full", models: "view", engineering: "full" , crm: true}),
  "Studio.Security": p({ key: "security", label: "Security", scopes: TEAM_SCOPES, areas: [...TEAM, "projects", "engineering", "security"], projects: "view", engineering: "view", approveConnections: true , crm: true}),
  // Marine, Carolyn's company: its own CRM that VERA builds and runs, photos from the glasses and the change log. Like the
  // server (Companies.cs), a customer of its own: no VERANeural areas.
  "Studio.Client.Marine": p({ key: "client-marine", label: "Marine", areas: ["home", "workspace", "records", "field", "activity", "files", "connections", "operations", "settings"], scopes: ["marine"], external: true, crm: true }),
  // Client packs (clientPacks.ts): DAMA Security and every client after it.
  ...Object.fromEntries(CLIENT_PACKS.map((c) => [c.role, p({ key: `client-${c.key}`, label: c.label, areas: [...c.areas, "settings"], scopes: [c.scope], external: true, crm: true, pack: c.key })])),
  "Studio.Investor": p({ key: "investor", label: "Investor", areas: [...BASE, "pages"], scopes: ["investor"], external: true, publishPages: true }),
  // EduTask, a customer company (EU and donor funding consultancy). Its own workspace only; no VERANeural areas, no student features.
  "Studio.Client.EduTask": p({ key: "client-edutask", label: "EduTask", areas: ["home", "workspace", "records", "activity", "proposals", "workflows", "files", "connections", "operations", "settings"], scopes: ["edutask"], external: true, crm: true }),
};

/**
 * Screens that are design mock-ups with no real function yet (Pages, Client Setup, Training Lab, Models, Security).
 * They are kept out of everyone's menu until they do something real.
 */
// Training Lab is real now (live: documents and VERA's rulebook), for Eva and the psychologist role.
// Projects was the sample platform overview; the team's real platforms, tasks and milestones are VERANeural HQ (Records, Home).
export const NOT_YET_REAL: AreaId[] = ["pages", "projects", "setup", "models", "security"];

/** Order of precedence when a person holds several roles: the first match wins. */
const PRECEDENCE = Object.keys(PROFILES);

/** A person with several roles gets the union of their areas and scopes, under the strongest profile. */
/** Roles that make someone a customer company's person. Like the server (Companies.cs), they get that company only. */
export const CUSTOMER_ROLES = ["Studio.Client.EduTask", "Studio.Client.Marine", ...CLIENT_PACKS.map((c) => c.role)];

/** A Marine partner's role (Studio.Client.Marine.{slug}): the partner's own company, the same Marine screens (see Companies.cs). */
export const isMarinePartnerRole = (r: string) => /^Studio\.Client\.Marine\.[a-z0-9]{2,30}$/.test(r);

export function resolveProfile(roles: string[]): Profile | null {
  const customer = CUSTOMER_ROLES.find((r) => roles.includes(r)) ?? (roles.some(isMarinePartnerRole) ? "Studio.Client.Marine" : undefined)
    ?? roles.map(packOfRole).find(Boolean)?.role;
  if (customer) { const c = { ...PROFILES[customer] }; c.areas = AREAS.map((a) => a.id).filter((id) => c.areas.includes(id) && !NOT_YET_REAL.includes(id)); return c; }
  const held = PRECEDENCE.filter((r) => roles.includes(r));
  if (!held.length) return null;
  const main = { ...PROFILES[held[0]] };
  for (const r of held.slice(1)) {
    const extra = PROFILES[r];
    main.areas = [...new Set([...main.areas, ...extra.areas])];
    main.scopes = [...new Set([...main.scopes, ...extra.scopes])];
    main.publishPages ||= extra.publishPages;
  }
  // Internal roles always win over external flags.
  main.external = held.every((r) => PROFILES[r].external);
  // Keep the area order of AREAS, without the mock-up screens.
  main.areas = AREAS.map((a) => a.id).filter((id) => main.areas.includes(id) && !NOT_YET_REAL.includes(id));
  return main;
}

/** Maps Studio scopes to VeraControlPlane workspace IDs. Investor, DAMA and VERA Core have no control-plane workspace yet, so they get no chat. */
export const CONTROL_PLANE_WORKSPACE: Partial<Record<ScopeId, { workspaceId: string; vertical?: string }>> = {
  all: { workspaceId: "ws-founder" },
  consumer: { workspaceId: "ws-consumer", vertical: "Consumer" },
  vds: { workspaceId: "ws-vds", vertical: "Vds" },
  marine: { workspaceId: "ws-marine", vertical: "Marine" },
  // Client packs: the server sends a licensed copy to its own workspace.
  ...Object.fromEntries(CLIENT_PACKS.map((c) => [c.scope, { workspaceId: c.workspaceId, vertical: "Client" }])),
  edutask: { workspaceId: "ws-edutask", vertical: "EduTask" },
};
